import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  HeaderContainer,
  HeaderContent,
  Logo,
  LogoImage,
  Nav,
  NavLink,
  ProductsNavLink,
  ProductsNavContainer,
  SearchContainer,
  SearchInput,
  SearchResults,
  SearchResultItem,
  HamburgerButton,
  MobileMenu,
  MobileNav,
  MobileNavLink,
} from './Header.styled';
import ProductsPopover from './ProductsPopover';
import {
  getAllProducts,
  getProductsByCategory,
} from '../ProductCategory/products';
import { CATEGORIES } from '../Categories/constant';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsHovered, setProductsHovered] = useState(false);
  const [arrowPosition, setArrowPosition] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const productsLinkRef = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const allProducts = getAllProducts();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const queryWords = query.split(/\s+/).filter((word) => word.length > 0);
      const resultsSet = new Set();
      const results = [];

      // Step 1: Check if query matches any category name
      // If it matches, add ALL products from that category
      const matchingCategories = CATEGORIES.filter((category) => {
        const categoryName = category.name.toLowerCase();
        return queryWords.every((word) => categoryName.includes(word));
      });

      matchingCategories.forEach((category) => {
        const categoryProducts = getProductsByCategory(category.slug);
        categoryProducts.forEach((product) => {
          if (!resultsSet.has(product.id)) {
            resultsSet.add(product.id);
            results.push({ product, score: 2, isCategoryMatch: true });
          }
        });
      });

      // Step 2: Also check for direct product name matches
      // This allows finding products by their name even if they're not in a matching category
      allProducts.forEach((product) => {
        if (resultsSet.has(product.id)) return; // Already added from category

        const productName = product.name.toLowerCase();

        // Check if all query words are in product name
        const nameMatch = queryWords.every((word) =>
          productName.includes(word)
        );

        if (nameMatch) {
          resultsSet.add(product.id);
          results.push({ product, score: 1, isCategoryMatch: false });
        }
      });

      // Sort by score (category matches first, then name matches)
      const sortedResults = results
        .sort((a, b) => b.score - a.score)
        .map((item) => item.product)
        .slice(0, 10);

      setSearchResults(sortedResults);
      setShowSearchResults(sortedResults.length > 0);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchQuery, allProducts]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProductClick = (product) => {
    navigate(`/products/${product.slug}`);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  useEffect(() => {
    const updateArrowPosition = () => {
      if (productsLinkRef.current) {
        const rect = productsLinkRef.current.getBoundingClientRect();
        const popoverContent = document.querySelector('[data-popover-content]');
        if (popoverContent) {
          const popoverRect = popoverContent.getBoundingClientRect();
          const productsCenter = rect.left + rect.width / 2;
          const popoverLeft = popoverRect.left;
          const arrowLeft = productsCenter - popoverLeft;
          setArrowPosition(arrowLeft);
        }
      }
    };

    if (productsHovered) {
      // Small delay to ensure popover is rendered
      const timeoutId = setTimeout(() => {
        updateArrowPosition();
      }, 10);

      window.addEventListener('resize', updateArrowPosition);
      window.addEventListener('scroll', updateArrowPosition);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', updateArrowPosition);
        window.removeEventListener('scroll', updateArrowPosition);
      };
    }
  }, [productsHovered]);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products', hasPopover: true },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const handleLogoClick = () => {
    navigate('/');
  };

  const mobileNavVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const mobileNavItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <HeaderContainer
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      style={{
        background: scrolled ? '#000000' : 'rgba(0, 0, 0, 0.95)',
        boxShadow: scrolled
          ? '0 4px 30px rgba(0, 0, 0, 0.3)'
          : '0 4px 20px rgba(0, 0, 0, 0.2)',
      }}
    >
      <HeaderContent>
        <Logo
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 400 }}
          onClick={handleLogoClick}
        >
          <LogoImage src="/img/swan-logo.png" alt="SWAN Logo" />
        </Logo>
        <Nav>
          {navItems.map((item, index) => {
            if (item.hasPopover) {
              return (
                <ProductsNavContainer
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                  onMouseEnter={() => setProductsHovered(true)}
                  onMouseLeave={() => setProductsHovered(false)}
                >
                  <ProductsNavLink
                    ref={productsLinkRef}
                    as={Link}
                    to={item.path}
                  >
                    {item.name}
                  </ProductsNavLink>
                  <AnimatePresence>
                    {productsHovered && (
                      <ProductsPopover
                        isVisible={productsHovered}
                        arrowPosition={arrowPosition}
                        onMouseEnter={() => setProductsHovered(true)}
                        onMouseLeave={() => setProductsHovered(false)}
                      />
                    )}
                  </AnimatePresence>
                </ProductsNavContainer>
              );
            }
            return (
              <NavLink
                key={item.name}
                as={Link}
                to={item.path}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.3 }}
              >
                {item.name}
              </NavLink>
            );
          })}
        </Nav>
        <SearchContainer ref={searchRef}>
          <SearchInput
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery && setShowSearchResults(true)}
          />
          <AnimatePresence>
            {showSearchResults && searchResults.length > 0 && (
              <SearchResults
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {searchResults.map((product) => (
                  <SearchResultItem
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                  >
                    {product.name}
                  </SearchResultItem>
                ))}
              </SearchResults>
            )}
          </AnimatePresence>
        </SearchContainer>
        <HamburgerButton
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          whileTap={{ scale: 0.95 }}
          animate={{
            rotate: mobileMenuOpen ? 90 : 0,
          }}
          transition={{ duration: 0.3 }}
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </HamburgerButton>
      </HeaderContent>
      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <MobileNav
              variants={mobileNavVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {navItems.map((item) => (
                <motion.div key={item.name} variants={mobileNavItemVariants}>
                  <MobileNavLink
                    as={Link}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </MobileNavLink>
                </motion.div>
              ))}
            </MobileNav>
          </MobileMenu>
        )}
      </AnimatePresence>
    </HeaderContainer>
  );
};

export default Header;
